import React, { useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Parallax } from 'react-scroll-parallax';
import { AnimatePresence, m } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Components
import fancyTextBoxData from '../../Components/FancyTextBox/FancyTextBoxData';
import SocialIcons from '../../Components/SocialIcon/SocialIcons';
import FancyTextBox from '../../Components/FancyTextBox/FancyTextBox';
import { fadeIn } from '../../Functions/GlobalAnimations';
import MessageBox from '../../Components/MessageBox/MessageBox';
import Buttons from '../../Components/Button/Buttons';
import GoogleMap from '../../Components/GoogleMap/GoogleMap';
import { sendEmail } from '../../Functions/Utilities';
import SideButtons from '../../Components/SideButtons';
import { Shield, MapPinned, Users, Mail, Phone } from "lucide-react"


const SocialIconsData = [
  {
    color: '#003479',
    link: 'https://www.facebook.com/profile.php?id=100072643861684&rdid=L6JcLbBYr4MJcy5A&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F16nxpWymQ9%2F',
    icon: 'fab fa-facebook-f',
  },
  {
    color: '#003479',
    link: 'https://www.youtube.com/@jaafaracademy',
    icon: 'fab fa-youtube',
  },
  {
    color: '#003479',
    link: 'https://www.instagram.com/jaafar_academy/?igsh=cjU1dWQ3MmQ5NWZq',
    icon: 'fab fa-instagram',
  },
  {
    color: '#003479',
    link: 'https://www.tiktok.com/@jaafaracademy_?_t=ZM-90jrTMt3iHI&_r=1',
    icon: 'fab fa-tiktok',
  },
];

const Contact = (props) => {
  const { t, i18n } = useTranslation();
  const formRef = useRef(null);
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';

  // بيانات IconWithText مترجمة
  const IconWithTextData = [
    {
      icon: Mail,
    titleKey: t('pricing_packages.icon_with_text.contact_help.title'),
    descriptionKey: t('pricing_packages.icon_with_text.contact_help.email'),
    linkTitleKey: t('pricing_packages.icon_with_text.contact_help.link_title'),
    link: 'mailto:contact@jaafaracademy.com',
      title: t('pricing_packages.icon_with_text.secure_payments.title'),
      content: t('pricing_packages.icon_with_text.secure_payments.content'),
    },
    {
      icon: Phone,
    titleKey: t('pricing_packages.icon_with_text.contact_us.title'),
    descriptionKey: t('pricing_packages.icon_with_text.contact_us.phone'),
    linkTitleKey: t('pricing_packages.icon_with_text.contact_us.link_title'),
      title: t('pricing_packages.icon_with_text.tailored_advice.title'),
      content: t('pricing_packages.icon_with_text.tailored_advice.content'),
    },
    {
      icon: MapPinned,
    titleKey: t('pricing_packages.icon_with_text.headquarters.title'),
    descriptionKey: t('pricing_packages.icon_with_text.headquarters.description'),
    linkTitleKey: t('pricing_packages.icon_with_text.headquarters.link_title'),
      title: t('pricing_packages.icon_with_text.customer_satisfaction.title'),
      content: t('pricing_packages.icon_with_text.customer_satisfaction.content'),
    },
  ];

  // États du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    comment: '',
    terms_condition: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const { fancyTextBox02 } = fancyTextBoxData;

  // Gestion des changements
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = t('contact.errors.name_required');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.email_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('contact.errors.email_invalid');
    }
    if (!formData.phone.trim()) newErrors.phone = t('contact.errors.phone_required');
    if (!formData.comment.trim()) newErrors.comment = t('contact.errors.comment_required');
    if (!formData.terms_condition) newErrors.terms_condition = t('contact.errors.terms_required');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await sendEmail(formData);
      if (response.status === 'success') {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          comment: '',
          terms_condition: false,
        });
        setErrors({});
        formRef.current.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ ...props.style, direction }}>
      <SideButtons />

      {/* Parallax Header */}
      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
              <Parallax
                className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
                translateY={[-40, 40]}
                style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
              />
              <Container className="h-full relative">
                <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
                  <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
                    <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
                {t('contact.page_title')}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>

      {/* قسم المزايا */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid min-md:grid-cols-3 gap-8">
            {IconWithTextData.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-4">
                    <Icon size={48} style={{ color: "#f29f2f" }} className="mx-auto" />
                  </div>
                  <h3 className="text-xxl font-bold mb-3 text-center" style={{ color: "#003479" }}>
                    {feature.titleKey}
                  </h3>
                  <p className="text-gray-600 text-center text-sm">{feature.descriptionKey}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      {/* نهاية قسم المزايا */}

      {/* Contact Form */}
      <m.section className="py-24 min-lg:py-20 min-md:py-16 min-sm:py-12" {...fadeIn}>
        <Container>
          <Row className="justify-center">
            <Col xl={6} lg={7} className="text-center mb-16 min-md:mb-12">
              <span className="font-serif mb-2 -tracking-tight text-lg block text-[#0879bd]">
                {t('contact.form_subtitle')}
              </span>
              <h4 className="font-serif font-semibold text-3xl min-md:text-4xl text-[#003479]">
                {t('contact.form_title')}
              </h4>
            </Col>

            <Col xl={10} className="col-sm-12 col-xs-12">
              <form ref={formRef} onSubmit={handleSubmit}>
                <Row className="grid grid-cols-1 min-md:grid-cols-2 gap-y-6 min-md:gap-y-8">
                  {/* Colonne gauche */}
                  <Col>
                    <div className="mb-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.form_name_placeholder')}
                        className="py-4 px-5 text-base w-full border border-[#d1d9e6] rounded-lg focus:border-[#f29f2f] focus:ring-2 focus:ring-[#f29f2f]/30 transition-all duration-300"
                      />
                      {errors.name && <p className="text-[#f29f2f] text-sm mt-1.5">{errors.name}</p>}
                    </div>

                    <div className="mb-6">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.form_email_placeholder')}
                        className="py-4 px-5 text-base w-full border border-[#d1d9e6] rounded-lg focus:border-[#f29f2f] focus:ring-2 focus:ring-[#f29f2f]/30 transition-all duration-300"
                      />
                      {errors.email && <p className="text-[#f29f2f] text-sm mt-1.5">{errors.email}</p>}
                    </div>

                    <div className="mb-6 min-md:mb-0">
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact.form_phone_placeholder')}
                        className="py-4 px-5 text-base w-full border border-[#d1d9e6] rounded-lg focus:border-[#f29f2f] focus:ring-2 focus:ring-[#f29f2f]/30 transition-all duration-300"
                      />
                      {errors.phone && <p className="text-[#f29f2f] text-sm mt-1.5">{errors.phone}</p>}
                    </div>
                  </Col>

                  {/* Colonne droite */}
                  <Col>
                    <div className="mb-6 min-md:mb-0">
                      <textarea
                        name="comment"
                        value={formData.comment}
                        onChange={handleChange}
                        rows="7"
                        placeholder={t('contact.form_comment_placeholder')}
                        className="border border-[#d1d9e6] w-full py-4 px-5 text-base h-[220px] resize-none rounded-lg focus:border-[#f29f2f] focus:ring-2 focus:ring-[#f29f2f]/30 transition-all duration-300"
                      />
                      {errors.comment && <p className="text-[#f29f2f] text-sm mt-1.5">{errors.comment}</p>}
                    </div>
                  </Col>

                  {/* Checkbox CGU */}
                  <Col className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
                    <label className="flex items-start cursor-pointer gap-3">
                      <input
                        type="checkbox"
                        name="terms_condition"
                        checked={formData.terms_condition}
                        onChange={handleChange}
                        className="mt-1.5 h-5 w-5 accent-[#f29f2f]"
                      />
                      <span className="text-sm leading-relaxed text-blue-900">
                        {t('contact.terms_condition_label')}
                      </span>
                    </label>
                    {errors.terms_condition && (
                      <p className="text-[#f29f2f] text-sm mt-1.5">{errors.terms_condition}</p>
                    )}
                  </Col>

                  {/* Bouton Envoyer */}
                  <Col className={i18n.language === 'ar' ? 'text-left' : 'text-right'}>
                    <Buttons
                      type="submit"
                      disabled={isSubmitting}
                      className={`btn-fill font-medium font-serif rounded-lg uppercase tracking-wide px-10 py-3.5 text-base transition-all duration-300 ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:brightness-110 hover:shadow-lg'
                      }`}
                      themeColor="#f29f2f"
                      color="#fff"
                      size="md"
                      title={isSubmitting ? t('contact.sending') : t('contact.submit_button')}
                    />
                  </Col>
                </Row>

                {/* Message de succès/erreur */}
                <AnimatePresence>
                  {status && (
                    <Row className="mt-10">
                      <Col xs={12}>
                        <m.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                          <MessageBox
                            className="py-3 px-5 rounded-lg border-l-4"
                            theme="message-box01"
                            variant={status === 'success' ? 'success' : 'danger'}
                            style={{
                              backgroundColor: status === 'success' ? '#e6f7ff' : '#fff5f5',
                              borderLeftColor: status === 'success' ? '#58bceb' : '#f29f2f',
                              color: status === 'success' ? '#003479' : '#003479',
                            }}
                            message={
                              status === 'success'
                                ? t('contact.success_message')
                                : t('contact.error_message')
                            }
                          />
                        </m.div>
                      </Col>
                    </Row>
                  )}
                </AnimatePresence>
              </form>
            </Col>
          </Row>
        </Container>
      </m.section>

      {/* Réseaux sociaux */}
      <section className="py-20 min-md:py-16 min-sm:py-12 bg-[#f8fbff]">
        <Container>
          <Row className="justify-center items-center gap-y-6">
            <Col
              xs={12}
              xl={3}
              lg={4}
              md={5}
              sm={6}
              className={
                i18n.language === 'ar'
                  ? 'min-lg:flex min-lg:justify-start min-xs:justify-center'
                  : 'min-lg:flex min-lg:justify-end min-xs:justify-center'
              }
            >
              <span className="font-serif font-medium text-2xl min-lg:text-xl text-[#003479] inline-block -tracking-tight">
                {t('contact.social_media')}
              </span>
            </Col>
            <Col xs={12} md={2} className="hidden min-sm:block">
              <span className="w-full h-px inline-block bg-[#d1d9e6]"></span>
            </Col>
            <Col xs={12} xl={3} lg={4} md={5} sm={6}>
              <SocialIcons
                theme="social-icon-style-01"
                className={
                  i18n.language === 'ar'
                    ? 'justify-end min-xs:justify-center'
                    : 'justify-start min-xs:justify-center'
                }
                size="sm"
                iconColor="dark"
                data={SocialIconsData}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Google Map */}
      <section>
        <GoogleMap
          className="p-0 min-md:h-[500px] min-xs:h-[350px]"
          location={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3237.2935083008!2d10.820064499999997!3d35.7681659!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13021371c41bbb55%3A0xb958c77be3d4a398!2sJAAFAR%20ACADEMY!5e0!3m2!1sfr!2stn!4v1767437080230!5m2!1sfr!2stn`}
        />
      </section>
    </div>
  );
};

export default Contact;